'use client';

import { ComponentType, ReactNode } from 'react';
import { redirect } from 'next/navigation';

import { ROUTES } from 'constant/routes';

import { useAppSelector } from 'store/hooks';

import { LocationFlowType } from 'types';

import { Loader } from 'ui/Loader';

interface AuthProps {
  children: ReactNode;
}

const withPublicRouting = (WrappedComponent: ComponentType<AuthProps>) => {
  const AuthComponent = ({ children, ...props }: AuthProps) => {
    const { userInfo } = useAppSelector((state) => state.persistAuth);
    const { user, isLoading, redirectFlow, isWelcomePage } = useAppSelector(
      (state) => state.user
    );
    const { redirectParams } = useAppSelector(
      (state) => state.bookingAppointment
    );

    const isUserFetching = isLoading || (userInfo && !user);

    const getRedirectPath = () => {
      if (isWelcomePage) {
        return ROUTES.welcome;
      }

      switch (redirectFlow) {
        case LocationFlowType.BOOK:
          return `${ROUTES.booking}?${redirectParams}`;
        case LocationFlowType.GIFT_CARDS:
          return ROUTES.giftCardsShopSteps.details;
        case LocationFlowType.PACKAGES_PROMO:
          return ROUTES.packagesShop;
        case LocationFlowType.PACKAGES:
          return ROUTES.packagesShop;
        case LocationFlowType.PRODUCT:
          return ROUTES.productsShop;
        case LocationFlowType.BASKET:
          return ROUTES.cart;
        case LocationFlowType.MEMBERSHIPS:
          return ROUTES.membershipShop;
        default:
          return ROUTES.dashboard;
      }
    };

    const getRedirect = () => {
      const path = getRedirectPath();

      redirect(path);
    };

    return user ? (
      getRedirect()
    ) : (
      <>
        <WrappedComponent {...props}>{children}</WrappedComponent>
        {isUserFetching && <Loader fixed bg='blackOpacity.10' />}
      </>
    );
  };

  return AuthComponent;
};

export default withPublicRouting;
